import { useEffect, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { FaxQueries } from '@frontend/api-fax';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, PopoverDialog, useForm, usePopoverDialog, DatePickerField, RadioField } from '@frontend/design-system';
import { useFaxSearchFiltersShallowStore } from '../../stores/fax-search-filters-store';
import { FaxNumbersMenuItem } from '../inbox/fax-numbers-filter-menu-item';
import { PopoverMenuSection } from '../inbox/filter-popover-section';
import { TagsFilterMenuItem } from '../inbox/tags-filter-menu-item';

type GlobalSearchFilterPopoverProps = {
  popoverProps: Pick<ReturnType<typeof usePopoverDialog<HTMLButtonElement>>, 'getDialogProps' | 'close'>;
  tags: Tag[];
};

export const FaxSearchFilterPopover = ({ popoverProps, tags }: GlobalSearchFilterPopoverProps) => {
  const { t } = useTranslation('fax');
  const { getDialogProps, close } = popoverProps;
  const { selectedLocationIds } = useAppScopeStore();
  const { data: FaxNumbersData } = FaxQueries.useQueryFaxNumbers(selectedLocationIds);
  const faxNumbers = FaxNumbersData?.map((item) => item.locationPhone) ?? [];
  const { faxSearchFilters, setFaxSearchFilters } = useFaxSearchFiltersShallowStore(
    'faxSearchFilters',
    'setFaxSearchFilters'
  );

  const [faxNumbersField, setFaxNumbersField] = useState<string[]>(faxSearchFilters.faxNumbers ?? []);
  const [tagsField, setTagsField] = useState<string[]>(faxSearchFilters.tags ?? []);

  const { getFieldProps, values, seedValues, changedValues, getErrors } = useForm({
    computeChangedValues: true,
    fields: {
      direction: {
        type: 'radio',
        value: faxSearchFilters.direction || '',
      },
      readStatus: {
        type: 'radio',
        value: faxSearchFilters.isRead || '',
      },
      deliveryStatus: {
        type: 'radio',
        value: faxSearchFilters.isDelivered || '',
      },
      startDate: {
        type: 'datePicker',
        value: faxSearchFilters.startDate || '',
        validator: ({ value }, formValues) => {
          if (!formValues.endDate) return '';
          const startDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(formValues.endDate.value, 'MM/DD/YYYY').startOf('day');

          return startDate.isAfter(endDate) ? t('Start date must be before the end date') : '';
        },
      },
      endDate: {
        type: 'datePicker',
        value: faxSearchFilters.endDate || '',
        validator: ({ value }, formValues) => {
          if (!formValues.startDate) return '';
          const startDate = dayjs(formValues.startDate.value, 'MM/DD/YYYY').startOf('day');
          const endDate = dayjs(value, 'MM/DD/YYYY').startOf('day');
          return endDate.isBefore(startDate) ? t('End date must be after the start date') : '';
        },
      },
    },
  });

  useEffect(() => {
    seedValues({
      direction: faxSearchFilters.direction || '',
      readStatus: faxSearchFilters.isRead || '',
      deliveryStatus: faxSearchFilters.isDelivered || '',
      startDate: faxSearchFilters.startDate || '',
      endDate: faxSearchFilters.endDate || '',
    });
    setTagsField(faxSearchFilters.tags ?? []);
    setFaxNumbersField(faxSearchFilters.faxNumbers ?? []);
  }, [faxSearchFilters, seedValues]);

  const handleSubmit = () => {
    setFaxSearchFilters({
      tags: tagsField ?? [],
      faxNumbers: faxNumbersField ?? [],
      direction: values.direction || '',
      isRead: values.readStatus || '',
      isDelivered: values.deliveryStatus || '',
      startDate: values.startDate || '',
      endDate: values.endDate || '',
    });
    close();
  };

  const errors = getErrors();
  const isError = !!errors.startDate || !!errors.endDate;

  const faxNumbersFieldChanged = !isEqual(faxNumbersField, faxSearchFilters.faxNumbers ?? []);
  const tagsFieldChanged = !isEqual(tagsField, faxSearchFilters.tags ?? []);

  const disableApply =
    isError ||
    (!faxNumbersFieldChanged &&
      !tagsFieldChanged &&
      (!changedValues || Object.keys(changedValues).every((key) => key === 'undefined')));

  const handleSelectTags = (selectedTags: string[] = []) => {
    const availableTagsCount = tags.length;
    const currentTags = tagsField ?? [];
    const updatedTags =
      availableTagsCount === currentTags.length && selectedTags.length === availableTagsCount
        ? []
        : [0, availableTagsCount].includes(selectedTags.length)
        ? selectedTags
        : currentTags.includes(selectedTags[0])
        ? currentTags.filter((n: string) => n !== selectedTags[0])
        : [...currentTags, selectedTags[0]];

    setTagsField(updatedTags);
  };

  const handleSelectFaxNumbers = (selectedNumbers: string[]) => {
    const availableFaxNumbersCount = faxNumbers.length;
    const currentFaxNumbers = faxNumbersField ?? [];
    const updatedNumbers =
      availableFaxNumbersCount === currentFaxNumbers.length && selectedNumbers.length === availableFaxNumbersCount
        ? []
        : [0, availableFaxNumbersCount].includes(selectedNumbers.length)
        ? selectedNumbers
        : currentFaxNumbers.includes(selectedNumbers[0])
        ? currentFaxNumbers.filter((n: string) => n !== selectedNumbers[0])
        : [...currentFaxNumbers, selectedNumbers[0]];

    setFaxNumbersField(updatedNumbers);
  };

  return (
    <PopoverDialog
      {...getDialogProps()}
      css={{
        width: 300,
        padding: theme.spacing(1, 2),
        borderRadius: theme.borderRadius.medium,
      }}
      returnFocus={false}
    >
      <>
        <PopoverMenuSection
          title={t('Read Status')}
          onClear={() => {
            getFieldProps('readStatus').onChange({ name: 'readStatus', value: '' });
          }}
          clearDisabled={!values.readStatus}
        >
          <RadioField
            {...getFieldProps('readStatus')}
            css={{ width: '100%', cursor: 'pointer', label: { cursor: 'pointer' }, input: { cursor: 'pointer' } }}
          >
            <RadioField.Radio value='read'>{t('Read')}</RadioField.Radio>
            <RadioField.Radio value='unread'>{t('Unread')}</RadioField.Radio>
          </RadioField>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Delivery Status')}
          onClear={() => {
            getFieldProps('deliveryStatus').onChange({ name: 'deliveryStatus', value: '' });
          }}
          clearDisabled={!values.deliveryStatus}
        >
          <RadioField
            {...getFieldProps('deliveryStatus')}
            css={{ width: '100%', cursor: 'pointer', label: { cursor: 'pointer' }, input: { cursor: 'pointer' } }}
          >
            <RadioField.Radio value='delivered'>{t('Delivered')}</RadioField.Radio>
            <RadioField.Radio value='undelivered'>{t('Undelivered')}</RadioField.Radio>
          </RadioField>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Relay Status')}
          onClear={() => {
            getFieldProps('direction').onChange({ name: 'direction', value: '' });
          }}
          clearDisabled={!values.direction}
        >
          <RadioField
            {...getFieldProps('direction')}
            css={{ width: '100%', cursor: 'pointer', label: { cursor: 'pointer' }, input: { cursor: 'pointer' } }}
          >
            <RadioField.Radio value='inbound'>{t('Inbound')}</RadioField.Radio>
            <RadioField.Radio value='outbound'>{t('Outbound')}</RadioField.Radio>
          </RadioField>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Send Date')}
          onClear={() => {
            getFieldProps('startDate').onChange({ name: 'startDate', value: '' });
            getFieldProps('endDate').onChange({ name: 'endDate', value: '' });
          }}
          clearDisabled={!values.startDate && !values.endDate}
        >
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <DatePickerField {...getFieldProps('startDate')} label={t('Start Date')} />
            <DatePickerField {...getFieldProps('endDate')} label={t('End Date')} />
          </div>
        </PopoverMenuSection>
        <PopoverMenuSection
          title={t('Other Filters')}
          onClear={() => {
            setFaxNumbersField([]);
            setTagsField([]);
          }}
          clearDisabled={!(tagsField?.length || faxNumbersField?.length)}
        >
          {faxNumbersField?.length > 1 && (
            <FaxNumbersMenuItem
              selectedFaxNumbers={faxNumbersField ?? []}
              selectFaxNumbers={handleSelectFaxNumbers}
              faxNumbers={faxNumbers}
            />
          )}
          <TagsFilterMenuItem
            tags={tags}
            selectedTags={tagsField ?? []}
            selectTags={handleSelectTags}
            closeParentMenu={close}
          />
        </PopoverMenuSection>
      </>
      <div css={{ padding: theme.spacing(1, 0) }}>
        <Button css={{ width: '100%' }} disabled={disableApply} onClick={handleSubmit}>
          {t('Apply')}
        </Button>
      </div>
    </PopoverDialog>
  );
};
